// Matches the API's MAX_SEARCH_LOCATIONS (Gitcha-API src/models/v2/wantad.join.model.js)
// so the UI never lets a buyer build a query the server would reject.
export const MAX_SEARCH_LOCATIONS = 5;

/**
 * Serializes the multi-city `locations` query param sent to
 * getWantAdListByFilters. Only produces a value once a second city is
 * added - a lone primary location keeps using the pre-existing
 * lat/long/city/state params untouched, so single-city search behavior
 * is unaffected.
 */
export function buildLocationsParam(primary, additional) {
  if (!additional || additional.length === 0) return undefined;
  if (!primary?.latitude || !primary?.longitude) return undefined;

  const all = [primary, ...additional].filter(
    (loc) => loc?.latitude && loc?.longitude,
  );
  if (all.length === 0) return undefined;

  return JSON.stringify(
    all.slice(0, MAX_SEARCH_LOCATIONS).map((loc) => ({
      lat: loc.latitude,
      long: loc.longitude,
      city: loc.city,
      state: loc.state,
    })),
  );
}

// Coordinate-based identity for a selected location. Keyed on lat/long
// (always present for a geocoded pick) rather than city/state, which can
// be undefined for address-type results and would otherwise collide with
// each other as "undefined|undefined".
function locationKey(loc) {
  return `${Number(loc?.latitude).toFixed(3)}|${Number(loc?.longitude).toFixed(3)}`;
}

// City/state identity, used alongside locationKey. A tenant-seeded default
// city (fixed lat/long from tenant config) and the same city re-picked via
// Places Autocomplete/Geocode can resolve to noticeably different
// coordinates (observed ~750m apart for the same city center), which
// locationKey's 3-decimal rounding doesn't cover - so two chips both
// labeled e.g. "Lubbock, TX" would otherwise slip past the duplicate check.
function cityStateKey(loc) {
  if (!loc?.city || !loc?.state) return null;
  return `${loc.city.trim().toLowerCase()}|${loc.state.trim().toLowerCase()}`;
}

function isSameLocation(a, b) {
  const aCityState = cityStateKey(a);
  const bCityState = cityStateKey(b);
  if (aCityState && bCityState) return aCityState === bCityState;
  return locationKey(a) === locationKey(b);
}

/**
 * Decides what a city pick from the search field should do, given the
 * current selection and whether Gitcha covers the picked city.
 *
 * - No usable primary yet (nothing selected, or the current primary isn't
 *   actually available - the not-available recovery screen is up): this
 *   pick REPLACES the primary, whether or not it's covered, so the
 *   not-available screen can be escaped by picking a covered city.
 * - A working primary already active: an unavailable pick is ignored, a
 *   covered duplicate/over-cap pick is ignored, otherwise it's appended.
 */
export function resolveLocationSelection({
  userLocation,
  additionalLocations,
  isAvailable,
  locationObject,
}) {
  const hasWorkingPrimary = !!userLocation?.isFromAvailableCities;

  if (!hasWorkingPrimary) {
    return {
      type: "replacePrimary",
      userLocation: { ...locationObject, isFromAvailableCities: isAvailable },
    };
  }

  if (!isAvailable) return { type: "noop" };

  if (1 + additionalLocations.length >= MAX_SEARCH_LOCATIONS) {
    return { type: "noop", reason: "cap" };
  }

  const alreadySelected = [userLocation, ...additionalLocations].some((loc) =>
    isSameLocation(loc, locationObject),
  );
  if (alreadySelected) return { type: "noop", reason: "duplicate" };

  return {
    type: "addAdditional",
    additionalLocations: [...additionalLocations, locationObject],
  };
}
